import { useMemo } from 'react';
import { useSet } from 'react-use';

import {
  ContextualUserGroupObjectType,
  ContextualUserGroupType,
} from '@/components/Form/components/VisibilitySettings/types';
import useEntitySet from '@/hooks/useEntitySet';
import useGroups from '@/modules/corporate/modules/group/views/GroupManager/hooks/useGroups';
import {
  GroupStatusEnum,
  GroupTypeEnum,
  VisibilitySettingsRestrictionTypeEnum,
} from '@/types/schema';

const useGroupsList = ({
  debounceSearchText,
  searchText,
  supportedContextualGroups,
  selectedContextualGroups,
  selectedGroupIds,
}: {
  debounceSearchText: string;
  searchText: string;
  supportedContextualGroups?: ContextualUserGroupObjectType[];
  selectedGroupIds: string[];
  selectedContextualGroups?: VisibilitySettingsRestrictionTypeEnum[];
}) => {
  const { selectedItemsSet: selectedGroupIdsSet, toggle: toggleSelectedGroupIds } =
    useEntitySet(selectedGroupIds);
  const [selectedContextualGroupsSet, { toggle: toggleSelectedContextualGroupIds }] = useSet(
    new Set(selectedContextualGroups),
  );

  const { groups, isFetching, fetchMore } = useGroups({
    filters: {
      name: debounceSearchText,
      systemGenerated: false,
      status: [GroupStatusEnum.Published],
      types: [GroupTypeEnum.UserCollection],
    },
  });

  const updatedGroups = useMemo(
    () => [
      ...(!searchText
        ? supportedContextualGroups?.map(
            (contextualUserGroup) =>
              ({
                type: 'ContextualUserGroupType',
                ...contextualUserGroup,
              }) as ContextualUserGroupType,
          ) ?? []
        : []),
      ...groups,
    ],
    [supportedContextualGroups, groups, searchText],
  );

  return {
    isGroupsFetching: isFetching,
    fetchMoreGroups: fetchMore,
    groups: updatedGroups,
    selectedGroupIdsSet,
    selectedContextualGroupsSet,
    toggleSelectedGroupIds,
    toggleSelectedContextualGroupIds,
  };
};

export default useGroupsList;
